/**
 * BoothPhotoStrip — Photo Booth flow inside Booth OS.
 *
 * Three-shot polaroid strip with countdown ceremony, preview reveal,
 * and dual save (3 raw originals + 1 composed strip variant). Lives
 * entirely inside `/booth-os/:event_id` — no tabs, no sibling routes,
 * no browser chrome exposure.
 *
 * Upload contract: each raw shot AND the composed strip POST to
 *   /api/events/{event_id}/photos/booth-guest?guest_id=booth_os
 * which is the same endpoint the existing /booth/{slug} page uses,
 * so strips land in the gallery, memory wall, and recap pipeline
 * exactly like every other booth capture.
 */
import React, { useState, useEffect } from 'react';
import { SERIF, Eyebrow, Heading, Lede, GoldPill, FlowFrame } from './primitives';
import { sendOrQueue, newDedupeId } from '../../lib/booth-os/uploadQueue';
import { useBoothCameraPipeline } from '../../hooks/useBoothCameraPipeline';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SHOT_COUNT = 3;
const COUNTDOWN_SECONDS = 3;
const INTERSHOT_DWELL_MS = 1100;

const BoothPhotoStrip = ({ theme, event, onBack, onComplete }) => {
  // 'intro' | 'countdown' | 'shooting' | 'review' | 'saving'
  const [stage, setStage] = useState('intro');
  const [shotIndex, setShotIndex] = useState(0);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [shots, setShots] = useState([]); // dataURLs
  const [strobing, setStrobing] = useState(false);

  // iter198 — Booth Camera Pipeline. Profile 'strip' shares the
  // booth profile's mirror + safe-zone vocabulary so every frame
  // in the strip composes from identical geometry across devices.
  const cam = useBoothCameraPipeline({ profile: 'strip' });
  const { videoRef, facingMode, error: cameraError, previewStyle, FramingGuide } = cam;

  useEffect(() => {
    if (stage === 'intro' || stage === 'countdown' || stage === 'shooting') {
      cam.start();
    } else if (stage === 'review' || stage === 'saving') {
      cam.stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const grabFrame = async () => cam.captureStill();

  // ── Countdown machine: 3 → 2 → 1 → shot → 1.1s dwell → repeat. ──
  useEffect(() => {
    if (stage !== 'countdown') return;
    if (countdown <= 0) {
      // captureStill is async (it lives on the camera pipeline);
      // wrap in an IIFE so the effect can stay synchronous while
      // the still settles into the strip.
      (async () => {
        const data = await grabFrame();
        if (!data) return;
        setStrobing(true);
        setTimeout(() => setStrobing(false), 220);
        const next = [...shots, data];
        setShots(next);
        if (next.length >= SHOT_COUNT) {
          // Final shot — small dwell so the guest sees the third frame
          // settle before the strip composes itself.
          setTimeout(() => setStage('review'), 700);
        } else {
          setTimeout(() => {
            setShotIndex((i) => i + 1);
            setCountdown(COUNTDOWN_SECONDS);
          }, INTERSHOT_DWELL_MS);
        }
      })();
      return;
    }
    const t = setTimeout(() => setCountdown((n) => n - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown, stage]);

  const startStrip = () => {
    setShots([]);
    setShotIndex(0);
    setCountdown(COUNTDOWN_SECONDS);
    setStage('countdown');
  };

  const retake = () => { startStrip(); };
  const flip = () => { cam.switchCamera(); };

  // ── Composite the 3 shots into one tall parchment strip canvas
  //    that mirrors the editorial polaroid aesthetic. Returns a Blob. ──
  const composeStripBlob = async () => {
    const STRIP_W = 600;
    const PAD = 32;
    const GAP = 22;
    const SHOT_W = STRIP_W - PAD * 2;
    const SHOT_H = Math.round(SHOT_W * (3 / 4));
    const FOOTER = 90;
    const STRIP_H = PAD * 2 + SHOT_H * SHOT_COUNT + GAP * (SHOT_COUNT - 1) + FOOTER;
    const canvas = document.createElement('canvas');
    canvas.width = STRIP_W;
    canvas.height = STRIP_H;
    const ctx = canvas.getContext('2d');
    // Parchment ground with grain
    ctx.fillStyle = theme.polaroidFrame || '#F5EAD2';
    ctx.fillRect(0, 0, STRIP_W, STRIP_H);
    // Subtle gold inner border
    ctx.strokeStyle = theme.gold || '#D4AF5E';
    ctx.lineWidth = 1;
    ctx.strokeRect(8, 8, STRIP_W - 16, STRIP_H - 16);

    const loadImg = (src) => new Promise((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = rej;
      im.src = src;
    });
    for (let i = 0; i < shots.length; i++) {
      const img = await loadImg(shots[i]);
      const y = PAD + i * (SHOT_H + GAP);
      // Cover-crop the source frame onto the slot.
      const srcRatio = img.width / img.height;
      const dstRatio = SHOT_W / SHOT_H;
      let sx = 0, sy = 0, sw = img.width, sh = img.height;
      if (srcRatio > dstRatio) {
        sw = img.height * dstRatio;
        sx = (img.width - sw) / 2;
      } else {
        sh = img.width / dstRatio;
        sy = (img.height - sh) / 2;
      }
      ctx.drawImage(img, sx, sy, sw, sh, PAD, y, SHOT_W, SHOT_H);
    }
    // Footer: event name + ✦
    const footerY = STRIP_H - FOOTER;
    ctx.fillStyle = '#3F2A14';
    ctx.font = 'italic 22px Georgia, "Times New Roman", serif';
    ctx.textAlign = 'center';
    ctx.fillText('✦ ' + (event.event_name || 'Tonight') + ' ✦', STRIP_W / 2, footerY + 38);
    ctx.font = 'italic 14px Georgia, "Times New Roman", serif';
    ctx.fillStyle = '#7A5A2E';
    ctx.fillText('EventDrop · A polaroid from the night', STRIP_W / 2, footerY + 64);

    return new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92);
    });
  };

  const dataUrlToBlob = (dataUrl) => fetch(dataUrl).then((r) => r.blob());

  const preserve = async () => {
    setStage('saving');
    try {
      // Each shot AND the composed strip carries its own dedupe_id.
      // If the network is dark, sendOrQueue writes to IDB and the
      // background heartbeat carries it onward when connectivity
      // returns. The guest journey completes either way.
      let anyDeferred = false;
      // iter211b ✦ — the strip IS the contribution. The composite's
      // dedupe_id is minted first and used as the primary so the
      // QR on the Personal Memory Page resolves directly to the
      // composed strip, not photo #1. (A guest never thinks "I
      // want photo #3 from my strip" — they think "I want my
      // strip".)
      const compositeDedupeId = newDedupeId();
      let primaryContributionId = compositeDedupeId;
      for (let i = 0; i < shots.length; i++) {
        const blob = await dataUrlToBlob(shots[i]);
        const fd = new FormData();
        fd.append('file', blob, `booth-strip-shot-${i + 1}-${Date.now()}.jpg`);
        const id = newDedupeId();
        fd.append('dedupe_id', id);
        const r = await sendOrQueue({
          kind: 'multipart',
          url: `${API}/events/${event.event_id}/photos/booth-guest?guest_id=booth_os&dedupe_id=${id}`,
          formData: fd,
          dedupeId: id,
        });
        if (!r.viaNetwork) anyDeferred = true;
      }
      // The composed strip — uploaded under the pre-minted
      // compositeDedupeId so the artefact endpoint can resolve it.
      composeStripBlob().then(async (compBlob) => {
        if (!compBlob) {
          // If composition somehow failed, fall back to the first
          // shot's dedupe so the QR still resolves to *something*.
          primaryContributionId = primaryContributionId || null;
          return;
        }
        const fd = new FormData();
        fd.append('file', compBlob, `booth-strip-composite-${Date.now()}.jpg`);
        fd.append('dedupe_id', compositeDedupeId);
        try {
          await sendOrQueue({
            kind: 'multipart',
            url: `${API}/events/${event.event_id}/photos/booth-guest?guest_id=booth_os_strip&dedupe_id=${compositeDedupeId}`,
            formData: fd,
            dedupeId: compositeDedupeId,
          });
        } catch (_) {}
      });
      // iter211 ✦ — route to the Personal Memory Page via the
      // composite's contribution_id. The 1.2s ceremony gives the
      // composite upload time to land before the QR resolves; if
      // the network is dark, sendOrQueue persists in IDB and the
      // resolution happens once connectivity returns.
      setTimeout(() => onComplete({
        deferred: anyDeferred,
        contribution_id: primaryContributionId,
      }), 1200);
    } catch (e) {
      // Only reachable if IDB itself blew up (private-mode etc).
      // eslint-disable-next-line no-console
      console.error('[BoothPhotoStrip] preserve failed', e?.message);
      setStage('review');
    }
  };

  return (
    <FlowFrame theme={theme} eyebrow="✦ CAPTURE THE ROOM" onBack={onBack} testId="booth-photo-strip">
      {stage === 'intro' && (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 26,
        }}>
          <Heading theme={theme}>Three photographs ✦</Heading>
          <Lede theme={theme}>
            One after the other. Hold a pose for each, and the strip composes itself.
          </Lede>
          <CameraFrame
            videoRef={videoRef} theme={theme} previewStyle={previewStyle}
            error={cameraError}
            FramingGuide={FramingGuide}
          />
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center' }}>
            <GoldPill theme={theme} large onClick={startStrip} testId="booth-strip-begin">
              Begin the strip ✦
            </GoldPill>
            <GoldPill theme={theme} secondary onClick={flip} testId="booth-strip-flip">
              Flip camera
            </GoldPill>
          </div>
        </div>
      )}

      {(stage === 'countdown' || stage === 'shooting') && (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 24,
          position: 'relative',
        }}>
          <Eyebrow theme={theme}>
            ✦ SHOT {shotIndex + 1} OF {SHOT_COUNT}
          </Eyebrow>
          <CameraFrame
            videoRef={videoRef} theme={theme} previewStyle={previewStyle}
            error={cameraError}
            FramingGuide={FramingGuide}
            overlay={
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: strobing ? 'rgba(245,234,210,0.92)' : 'transparent',
                transition: 'background 200ms ease',
              }}>
                {!strobing && (
                  <div style={{
                    fontFamily: SERIF, fontWeight: 500,
                    fontSize: 'clamp(80px, 18vw, 220px)',
                    color: theme.gold,
                    textShadow: '0 6px 30px rgba(0,0,0,0.6)',
                    lineHeight: 1,
                  }}>{countdown > 0 ? countdown : ''}</div>
                )}
              </div>
            }
          />
          <ShotIndicator theme={theme} count={SHOT_COUNT} index={shots.length} />
        </div>
      )}

      {stage === 'review' && (
        <div style={{
          flex: 1, display: 'grid',
          // Mobile-safe — single column at narrow widths, side-by-side
          // when viewport allows. The previous `minmax(220px, 1fr)`
          // forced a two-column minimum of ~500px which overflowed
          // 390px phones.
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))',
          gap: 'clamp(24px, 4vw, 56px)',
          alignItems: 'center', justifyItems: 'center',
          width: '100%',
        }}>
          <StripPreview shots={shots} theme={theme} event={event} />
          <div style={{ maxWidth: 420 }}>
            <Eyebrow theme={theme}>✦ HOLD THE STRIP</Eyebrow>
            <Heading theme={theme}>Three frames from tonight ✦</Heading>
            <Lede theme={theme} center={false}>
              If they hold what you wanted, preserve them. Or take another strip — the night is in no hurry.
            </Lede>
            <div style={{ display: 'flex', gap: 12, marginTop: 26, flexWrap: 'wrap' }}>
              <GoldPill theme={theme} large onClick={preserve} testId="booth-strip-preserve" style={{ minWidth: 200 }}>
                Preserve the strip ✦
              </GoldPill>
              <GoldPill theme={theme} secondary onClick={retake} testId="booth-strip-retake">
                Take another
              </GoldPill>
            </div>
          </div>
        </div>
      )}

      {stage === 'saving' && (
        <div data-testid="booth-strip-saving" style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 16,
        }}>
          <Eyebrow theme={theme}>✦ PRESERVING</Eyebrow>
          <Heading theme={theme}>The strip is settling onto the wall ✦</Heading>
        </div>
      )}
    </FlowFrame>
  );
};

// ── Shared kiosk camera frame ─────────────────────────────────────
const CameraFrame = ({ videoRef, theme, previewStyle, error, overlay, FramingGuide }) => {
  const frameRef = React.useRef(null);
  return (
    <div ref={frameRef} style={{
      width: 'min(620px, 90vw)', aspectRatio: '4 / 3',
      borderRadius: 14, overflow: 'hidden',
      background: '#000', border: `1px solid ${theme.hair}`,
      boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
      position: 'relative',
    }}>
      <video
        ref={videoRef} playsInline muted
        style={previewStyle}
      />
      {FramingGuide && <FramingGuide longPressTarget={frameRef} />}
      {overlay}
      {error && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: theme.ink, fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 15, textAlign: 'center', padding: 32,
        }}>{error}</div>
      )}
    </div>
  );
};

const ShotIndicator = ({ theme, count, index }) => (
  <div style={{ display: 'flex', gap: 10 }} aria-label={`Shot ${index + 1} of ${count}`}>
    {Array.from({ length: count }).map((_, i) => (
      <span
        key={i}
        style={{
          width: 36, height: 3, borderRadius: 2,
          background: i < index ? theme.gold : 'rgba(212,170,82,0.18)',
          transition: 'background 320ms ease',
        }}
      />
    ))}
  </div>
);

const StripPreview = ({ shots, theme, event }) => (
  <div style={{
    background: theme.polaroidFrame,
    padding: '14px 14px 22px',
    borderRadius: 6,
    boxShadow: '0 24px 60px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.55)',
    display: 'flex', flexDirection: 'column', gap: 8,
    width: 'min(260px, 38vw)',
  }}>
    {shots.map((s, i) => (
      <img
        key={i}
        src={s}
        alt={`Shot ${i + 1}`}
        style={{
          width: '100%', aspectRatio: '4/3',
          objectFit: 'cover', borderRadius: 2,
          display: 'block',
        }}
      />
    ))}
    <div style={{
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 11, color: '#3F2A14',
      letterSpacing: '0.16em', textTransform: 'uppercase',
      textAlign: 'center', marginTop: 8,
    }}>
      ✦ {event.event_name || 'Tonight'} ✦
    </div>
  </div>
);

export default BoothPhotoStrip;
